import { Injectable, Injector } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { catchError, map, of } from 'rxjs';
import { ApiNotificationsService, MobilePlatform } from '../api';
import { AuthRepository } from '../auth';
import { ILogger, LoggerService } from '../logging';
import { PluginNames } from '../models';

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  private logger: ILogger;
  private authRepo: AuthRepository;
  constructor(
    injector: Injector,
    private api: ApiNotificationsService,
    loggerService: LoggerService,
  ) {
    this.authRepo = injector.get(AuthRepository);
    this.logger = loggerService.getLogger('PushNotificationService');
  }

  async register(): Promise<boolean> {
    if (!Capacitor.isPluginAvailable(PluginNames.PushNotifications)) return false;

    if (this.authRepo.getHasRegisteredToPushRecently()) {
      this.logger.warn('Already Registered to Push Notifications this day');
      return true;
    }

    const capPlatform = Capacitor.getPlatform();
    const mobilePlatform = capacitorPlatformToMobilePlatform(capPlatform);
    if (!mobilePlatform) {
      this.logger.warn(`Push plugin is enabled but cannot convert Capacitor platform '${capPlatform}' to mobilePlatform`);
      return false;
    }

    await PushNotifications.removeAllListeners();
    await PushNotifications.addListener('registration', (token) => {
      this.logger.debug('Token registration created ', token.value);
      this.api
        .postRegisterDevice({
          body: { deviceToken: token.value, platform: mobilePlatform },
        })
        .pipe(
          map(() => true),
          catchError((e) => {
            this.logger.warn('An error occured while trying to PostRegisterDevice', e);
            return of(false);
          }),
        )
        .subscribe((result) => {
          if (result) {
            this.logger.info('PostRegisterDevice done successfully');
            this.authRepo.setLastPushRegister(new Date());
          }
        });
    });

    await PushNotifications.addListener('registrationError', (err) => {
      this.logger.error('Registration error: ', err.error);
    });

    await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      this.logger.info('Push notification received: ', notification);
    });

    await PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      this.logger.info('Push notification action performed', notification.actionId, notification.inputValue);
    });

    this.logger.debug('Registration of events for PushNotifications done');
    return await this.internalRegister();
  }

  private async internalRegister() {
    const result = await PushNotifications.requestPermissions();
    if (result.receive === 'granted') {
      this.logger.info('Push Notifications permissions granted');
      await PushNotifications.register();
      return true;
    } else {
      this.logger.warn('Push Notifications permissions denied');
      return false;
    }
  }

  async unregister() {
    if (!Capacitor.isPluginAvailable(PluginNames.PushNotifications)) return;

    // Unregister not implemented yet, we only remove all Listeners atm
    this.logger.info('Unregistering from Push Notifications (only events)');
    this.authRepo.setLastPushRegister(null);
    await PushNotifications.removeAllListeners();
  }

  async hasAcceptedPushNotifications(): Promise<boolean> {
    if (!Capacitor.isPluginAvailable(PluginNames.PushNotifications)) return false;

    const result = await PushNotifications.checkPermissions();
    return result.receive === 'granted';
  }
}

const capacitorPlatformToMobilePlatform = (platform: string): MobilePlatform | null => {
  switch (platform) {
    case 'android':
      return MobilePlatform.Android;
    case 'ios':
      return MobilePlatform.Ios;

    default:
      return null;
  }
};
